import React, { useContext, useState, useRef }  from 'react'
import { View, StyleSheet } from 'react-native'
import { Icon, Text, Radio, Button, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { FontAwesome5 } from '@expo/vector-icons'
import { useTranslation } from 'react-i18next'
import { getExternalDestinationInfo } from './PaymentTransactionDetailScreen'
import { currencyFormatterAtom, localeDateServiceAtom, storesDictAtom, suppliersDictAtom, userRoleAtom, usersDictAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import OperationsList from '../../components/OperationsList'
import { format } from 'date-fns'
import { getLocalStoreDate } from '../../utils/timezoneFunctions'
import { paymentTransactionDate, paymentTransactionRange, paymentRangeOverlaps } from './paymentTransactionDates'

export default function PaymentTransactionsScreen({ navigation, supplierID=null, initialRange={startDate: new Date(), endDate: new Date()}, showTotal=false }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [context] = useContext(UserContext)
  const usersDict = useAtomValue(usersDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const [queryType, setQueryType] = useState('createdDate')
  const operationsListRef = useRef()

  // headerRight "+" button is handled by OperationsList via showAddButton prop
  // OperationsList passes addItemToData callback so new items appear immediately

  /**
   * ATL-986: declared for BOTH read paths. The `api` half is not optional — the
   * Supabase read path used to receive nothing here and listed every transaction,
   * income included, and (from SupplierDetailScreen) every supplier's payments
   * under one supplier's total.
   */
  const additionalFilters = {
    firestore: (query) => {
      query = query.where('type', '==', 'expense')

      if (supplierID != null) {
        query = query.where('externalDestination.id', '==', supplierID)
      }

      return query
    },
    api: {
      type: 'expense',
      ...(supplierID != null ? { externalDestinationId: supplierID } : {}),
    },
  }

  /**
   * ATL-1313 — every date on a row is DECODED before it reaches `format()`.
   *
   * `format()` throws `RangeError: Invalid time value` on anything that is not a valid Date, and
   * this function runs inside JSX, outside `OperationsList`'s own try/catch — so one unreadable
   * date here is a React render error, which is fatal on native, which relaunches the app into
   * the same list. The pre-fix expression was `x?.toDate?.() || x`, correct only against a
   * Firestore Timestamp and a no-op against the two encodings the Supabase read path actually
   * returns. See `paymentTransactionDates.ts` for the three encodings and their row counts.
   *
   * `—` is what an unreadable date looks like. Nothing is substituted for it: an invented date
   * reads as a real one (ATL-868).
   */
  const formatStoreDate = (store, date, pattern) => (date ? format(getLocalStoreDate(store, date), pattern) : '—')

  const renderItemFieldsToShow = (item) => {
    const itemDate = paymentTransactionDate(item)
    const itemRange = paymentTransactionRange(item)
    return (
      <>
        <View style={styles.dateTimeView}>
          <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text style={styles.itemTitle} category='s2'>
            {formatStoreDate(item.store, itemDate, "d/M/y HH:mm")}
          </Text>
        </View>
        <View style={styles.dateTimeView}>
          <Icon name='calendar-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text style={styles.itemTitle} category='s2'>
            {itemRange ? `${formatStoreDate(item.store, itemRange.startDate, "d/M/y EEE")} - ${formatStoreDate(item.store, itemRange.endDate, "d/M/y EEE")}` : '-'}
          </Text>
        </View>
        <View style={styles.dateTimeView}>
          <Icon name='bookmark-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text style={styles.itemTitle} category='s2'>
            {t('payments.' + item.linkedDocType + 'Payment')}
          </Text>
        </View>
        <View style={styles.dateTimeView}>
          <Icon name='person-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text style={styles.itemTitle} category='s2'>
            {getExternalDestinationInfo(item, usersDict, suppliersDict).text}
          </Text>
        </View>
        <View style={styles.dateTimeView}>
          <Icon name='pricetags-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text style={styles.itemTitle} category='s2'>
            {currencyFormatter.format(item.value)}
          </Text>
        </View>
        <View style={styles.dateTimeView}>
          <FontAwesome5 name="building" size={16} color={theme['text-basic-color']} style={styles.icon} />
          <Text style={styles.itemTitle} category='s2'>
            {storesDict?.[item.store]?.name}
          </Text>
        </View>
      </>
    )
  }

  const topView = () => {
    return (
      <>
        {(userRole?.permissions?.['Stats'] || context.user.role === 'admin') && 
          <Button style={{alignSelf: 'flex-end'}} appearance='ghost' size='small' onPress={() => {
            navigation.navigate('PaymentStats')
          }} accessoryLeft={(props) => (<Icon name='bar-chart-outline' {...props} style={{...props.style, marginRight: 0}} />)}>
            {t('payments.stats')}
          </Button>
        }
        <View style={styles.dateFilter}>
          <Text category='label' style={styles.label}>
            {t('filterByDate')}
          </Text>
          <Radio style={styles.radio} checked={queryType === 'createdDate'} onChange={() => {
            setQueryType('createdDate')
            operationsListRef.current.refresh(initialRange || {}, additionalFilters)
          }}>
            {t('payments.byCreatedDate')}
          </Radio>
          <Radio style={styles.radio} checked={queryType === 'paymentPeriod'} onChange={() => {
            setQueryType('paymentPeriod')
            operationsListRef.current.refresh(initialRange || {}, additionalFilters)
          }}>
            {t('payments.byPaymentPeriod')}
          </Radio>
        </View>
      </>
    )
  }

  return (
    <View style={styles.container}>
      <OperationsList 
        collectionName='transactions'
        dateField={queryType === 'createdDate' ? 'date' : 'paymentRange.endDate'}
        additionalFilters={additionalFilters}
        detailScreenName='PaymentTransactionDetail'
        topView={topView}
        initialRange={initialRange}
        showTotalSum={showTotal}
        totalSumFormatter={currencyFormatter}
        totalSumField='value'
        ref={operationsListRef}
        showAddButton={showTotal ? false : true}
        additionalDataValidation={(data, range, store) => {
          // Filter out transactions that don't match the payment period range.
          //
          // ATL-1313: the comparison used to run over UNDECODED values. `'2026-09-09 23:29:55+00'
          // <= Date` and `{_seconds:…} <= Date` do not throw — they coerce and answer false — so
          // this quietly dropped every migrated and every RPC-written payment from the
          // payment-period view. A list that omits payments without saying so is worse than one
          // that crashes; `paymentRangeOverlaps` decodes first.
          if (queryType === 'paymentPeriod' && range !== 'allTime' && range.startDate && range.endDate) {
            return data.filter(section => {
              section.data = section.data.filter(item => paymentRangeOverlaps(item, range));
              return section.data.length > 0;
            });
          }
          return data;
        }}
        detailScreenParams={{}}
        renderItemFieldsToShow={renderItemFieldsToShow}
        navigation={navigation}
        showStoreFilter={true}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  dateFilter: {
    marginHorizontal: 20,
  },
  icon: {
    marginRight: 4,
  },
  itemTitle: {
    marginRight: 25,
  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  label: {
    marginTop: 10,
    marginBottom: 5
  },
  radio: {
    marginBottom: 5
  }
})
